<template lang="pug">
  header#page-header(:class="stickyHeaderClass")
    .container.mx-auto.wrap
      .logo
          component(:is="logoWrap" v-bind="logoWrapProps")
            img(
              v-if="logo && logo.id" 
              :src="image.src" 
              alt="LSE-Software" 
              rel="noopener"
            )
      .menu
        Menu#main-menu(
          k="main-menu" 
          :max-depth="2"
        )
      button.hamburger.hamburger--3dy(
        type="button" 
        :class="{ 'is-active': sidebar }" 
        @click="toggleSidebar"
      )
          span.hamburger-box
            span.hamburger-inner
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { trim } from '@/helpers';
import Menu from '@/components/Menu.vue';

export default {
    name: 'Header',
    data: () => ({
        lastScroll: 0,
        stickyHeaderClass: ''
    }),
    components: {
        Menu
    },
    computed: {
        logoWrap() {
            let tag = 'div';
            if (this.$route.path !== '/') {
                tag = 'a';
            }
            return tag;
        },
        logoWrapProps() {
            let props = {};
            if (this.$route.path !== '/') {
                props = { href: '/' };
            }
            return props;
        },
        image() {
            const storageRoute = trim(
                `${process.env.VUE_APP_API_HTTP}${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`,
                '/'
            );

            const devicePixelRatio = 280 * window.devicePixelRatio;
            const fit = 'fit&w=' + devicePixelRatio;

            let width = this.logo.width;
            let height = this.logo.height;

            return {
                src: `${storageRoute}/images/${this.logo.id}/${this.logo.name}?${fit}`,
                error: `${storageRoute}/images/${this.logo.id}/${this.logo.name}?${fit}&preview=greyscale`,
                loading: `${storageRoute}/images/${this.logo.id}/${this.logo.name}?${fit}&preview=blur`,
                width,
                height
            };
        },
        ...mapGetters({
            logo: 'logo',
            menus: 'menus',
            sidebar: 'sidebar'
        })
    },
    methods: {
        toggleSidebar() {
            this.updateSidebarVisibility(!this.sidebar);
        },

        updateStickyHeader() {
            const currentScroll = window.pageYOffset;

            const scrollUp = 'scroll-up';
            const scrollDown = 'scroll-down';

            if (currentScroll > this.lastScroll && this.stickyHeaderClass !== scrollDown) {
                this.stickyHeaderClass = scrollDown;
            } else if (currentScroll < this.lastScroll && this.stickyHeaderClass !== scrollUp) {
                this.stickyHeaderClass = scrollUp;
            }

            this.lastScroll = currentScroll;
        },
        ...mapMutations({
            updateSidebarVisibility: 'updateSidebarVisibility'
        })
    },
    mounted() {
        window.addEventListener('scroll', this.updateStickyHeader);
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.updateStickyHeader);
    }
};
</script>

<style lang="sass" nolang="scss" scoped>
@import "~@/assets/sass/variables"

header
  @apply bg-white
  box-shadow: 0 0 3px 0 rgba(0,0,0,0.22)
  .wrap
    @apply flex justify-between flex-wrap content-center
    min-height: 60px
    @screen lg
      min-height: 160px
    .logo
      img
        width: 130px
        margin-top: 18px
        height: auto
        @screen lg
          margin-top: 0
          width: 280px
    .menu
      @apply flex flex-wrap content-center
      #main-menu
        @apply hidden
        @screen md
          @apply flex
        &::v-deep
          ul
            @apply hidden
            @screen lg
              @apply flex 
            &.tree-menu
              & > li
                @apply relative transition duration-300 ease-linear
                margin: 0 10px
                &:last-child
                  margin-right: 0 !important
                & > .tree-item a
                  @apply flex text-lg font-bold
                  color: $color-primary
                  .svg-inline--fa
                    @apply hidden
                  &:after
                    @apply absolute block left-0 w-full
                    content: ''
                    transition: transform .3s ease-out,border-color .3s ease-out
                    border-top: 2px solid $color-base-text
                    bottom: -2px
                    transform: scaleX(0)
                  
              & > li.active a:after, li.hover a:after
                    transform: scaleX(1)
          & > ul
            &.tree-menu
              & > li
                & > ul
                  @apply bg-white border border-solid
                  border-color: #dad9d4
                  > li
                    @apply m-0
                    &:last-child a
                      border: 0
                  &.tree-menu
                    @apply absolute left-0 inline-block
                    top: 40px
                    z-index: 100
                    min-width: 12em
                    margin: 0
                    a
                      @apply text-black font-normal text-base leading-none h-auto border-b border-solid normal-case
                      border-color: #dad9d4
                      padding: 0.625em
                      &:after
                        @apply hidden
                      &:hover
                        @apply bg-gray-200
                        color: $color-primary
              ul ul
                @apply absolute top-0
                min-width: 12em
                left: 100%
    .hamburger
      @screen lg
        @apply hidden
      & ::v-deep
        .hamburger-box
          .hamburger-inner,
          .hamburger-inner:before,
          .hamburger-inner:after
            background-color: $color-primary

</style>
